// Portal Module Presets — admin CRUD. Presets bundle modules that can be
// applied to client portals. Future modules appear here automatically.
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Loader2, Star, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalModuleSelector, { resolveDeps } from "@/components/PortalModuleSelector";
import Pill from "@/components/Pill";

const ADMIN_ROLES = new Set(["director", "administrator"]);

export default function PortalPresetsPage() {
    const { user } = useAuth();
    const canEdit = user && ADMIN_ROLES.has(user.role);
    const [catalog, setCatalog] = useState(null);
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState(false);
    const [dialog, setDialog] = useState(null);

    const load = () => {
        setLoading(true);
        Promise.all([api.get("/portal-modules/catalog"), api.get("/portal-presets")])
            .then(([cat, ps]) => { setCatalog(cat.data); setPresets(ps.data); })
            .catch((e) => { if (e.response?.status === 403) setDenied(true); })
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const labelFor = (id) => {
        for (const g of catalog?.groups || []) for (const m of g.modules) if (m.id === id) return m.label;
        return id;
    };

    async function remove(p) {
        if (!window.confirm(`Delete preset "${p.name}"? Clients already using it keep their current modules.`)) return;
        try { await api.delete(`/portal-presets/${p.id}`); toast.success("Preset deleted"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }

    if (denied) {
        return <div data-testid="portal-presets-page" className="py-12 text-center font-serif-italic text-[#9A968F]">Admin-only section.</div>;
    }

    return (
        <div data-testid="portal-presets-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>Admin · Portal Modules</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Portal Module Presets</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        Reusable module bundles applied to client portals. New modules appear here automatically as they ship.
                    </p>
                </div>
                {canEdit && (
                    <button data-testid="preset-new-button" onClick={() => setDialog({ mode: "create", p: { name: "", description: "", module_ids: catalog?.core || [], is_default: false } })}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        <Plus className="w-3.5 h-3.5" /> New preset
                    </button>
                )}
            </div>

            {loading ? (
                <div className="py-10 text-center text-[#9A968F]"><Loader2 className="inline w-4 h-4 animate-spin mr-2" />Loading…</div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {presets.map((p) => (
                        <div key={p.id} data-testid={`preset-card-${p.id}`} className="bg-white border rounded-[12px] p-4 shadow-brand-sm flex flex-col" style={{ borderColor: "#E5E0DA" }}>
                            <div className="flex items-start gap-2">
                                <div className="w-9 h-9 rounded-[9px] grid place-items-center flex-shrink-0" style={{ background: "#FBEFE5", color: "#9F4212" }}><Layers className="w-4.5 h-4.5" /></div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-[#1F1F1F] flex items-center gap-1.5">{p.name}
                                        {p.is_default && <span title="Default preset"><Star className="w-3.5 h-3.5 fill-[#C68A1C] text-[#C68A1C]" /></span>}
                                    </div>
                                    {p.description && <div className="text-[11.5px] mt-0.5 leading-snug" style={{ color: "#6F6A64" }}>{p.description}</div>}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-3">
                                {p.module_ids.slice(0, 6).map((id) => <Pill key={id} tone="neutral">{labelFor(id)}</Pill>)}
                                {p.module_ids.length > 6 && <Pill tone="accent">+{p.module_ids.length - 6} more</Pill>}
                            </div>
                            {canEdit && (
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: "#F0EDE8" }}>
                                    <button data-testid={`preset-edit-${p.id}`} onClick={() => setDialog({ mode: "edit", p })} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}><Pencil className="w-3 h-3" /> Edit</button>
                                    <button data-testid={`preset-delete-${p.id}`} onClick={() => remove(p)} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-[#B33A2E] hover:bg-[#FBE8E5]"><Trash2 className="w-3 h-3" /> Delete</button>
                                </div>
                            )}
                        </div>
                    ))}
                    {presets.length === 0 && <div className="col-span-full py-8 text-center italic text-[#9A968F]">No presets yet.</div>}
                </div>
            )}

            {dialog && catalog && (
                <PresetDialog mode={dialog.mode} p={dialog.p} catalog={catalog}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
            )}
        </div>
    );
}

function PresetDialog({ mode, p, catalog, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: p.name || "", description: p.description || "",
        module_ids: [...new Set([...(p.module_ids || []), ...(catalog.core || [])])],
        is_default: !!p.is_default,
    });
    const [busy, setBusy] = useState(false);

    function setModules(arr, added) {
        setForm((f) => ({ ...f, module_ids: arr }));
        if (added?.length) toast.info("Required supporting modules have also been enabled.");
    }

    async function save() {
        if (!form.name.trim()) { toast.error("Preset name is required"); return; }
        setBusy(true);
        try {
            const body = { name: form.name.trim(), description: form.description || null, module_ids: form.module_ids, is_default: form.is_default };
            if (mode === "create") await api.post("/portal-presets", body);
            else await api.patch(`/portal-presets/${p.id}`, body);
            toast.success(mode === "create" ? "Preset created" : "Preset updated");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="preset-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[680px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h2 className="font-display text-[18px] font-bold text-[#1F1F1F]">{mode === "create" ? "New preset" : "Edit preset"}</h2>
                    <button data-testid="preset-dialog-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-5 space-y-4 overflow-y-auto">
                    <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Preset name</span>
                        <input data-testid="preset-field-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. High-Rise / Building Safety" className="prs-fi" /></label>
                    <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Description</span>
                        <input data-testid="preset-field-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Optional summary" className="prs-fi" /></label>
                    <label className="flex items-center gap-2 text-[12.5px] font-medium text-[#2D2D2D]">
                        <input data-testid="preset-field-default" type="checkbox" checked={form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} /> Set as default preset
                    </label>
                    <div>
                        <div className="text-[11px] font-semibold mb-2" style={{ color: "#6F6A64" }}>Modules in this preset</div>
                        <PortalModuleSelector catalog={catalog} value={form.module_ids} onChange={setModules} idPrefix="preset-module" />
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex items-center justify-end gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid="preset-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save preset"}</button>
                </div>
                <style>{`.prs-fi{width:100%;padding:8px 11px;background:#fff;border:1px solid #E5E0DA;border-radius:8px;font-size:12.5px;color:#1F1F1F;outline:none;}.prs-fi:focus{border-color:#C75A1B;box-shadow:0 0 0 3px #F6E4D8;}`}</style>
            </div>
        </div>
    );
}
